import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CalendarDays, Clock, Video, Check, X, ChevronDown, ArrowRight, Loader2, Target, ShieldCheck, Timer, Compass,
  MessageCircle, CalendarPlus, MailCheck, UserCheck, Radio,
} from 'lucide-react';
import { BACKEND_URL } from '@/config/api';
import { useReferral } from '@/hooks/useReferral';
import { useFormToken, honeypotStyle } from '@/hooks/useFormToken';

/*
  /masterclass-en-directo — registro a la próxima masterclass online (Samuel, 02/11/2026).

  Genérica: muestra el próximo evento con formato «online» publicado en Admin › Eventos (título, fecha y hora).
  El enlace de Zoom nunca sale aquí: llega por email (confirmación + recordatorios 24 h y 1 h antes, backend/masterclass.py).
  Registro lo más suave posible: solo nombre y email. Los afiliados la comparten con ?ref= y la inscripción queda a su nombre.

  Estructura Hormozi: arriba del todo la promesa y el registro; después la ecuación de valor (resultado, probabilidad,
  tiempo, esfuerzo), el viejo camino frente al nuevo, qué se ve en la sesión, para quién es, quién la da, preguntas
  y CTA final. Todos los CTA bajan al formulario.
  Reglas FHC: club privado y educativo; sin precios, cifras, rentabilidades ni tokens; el socio decide.
*/

const API = BACKEND_URL;
const GOLD = 'text-[#c9a84c]';
const serif = { fontFamily: "'Playfair Display', Georgia, serif" };
const DEFAULT_TITLE = 'Cómo acceder al mercado inmobiliario sin comprar un piso entero';

const reveal = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.55, ease: 'easeOut' },
};

const LEARN = [
  'Por qué el mercado inmobiliario de calidad ha estado siempre en manos de unos pocos, y qué ha cambiado.',
  'Cómo un club privado permite formar parte de operaciones inmobiliarias reales sin comprar una vivienda entera.',
  'Qué mirar en una operación antes de decidir nada: el criterio que usamos en el club.',
  'Cómo se forma un miembro desde cero, a su ritmo y con acompañamiento.',
  'Cómo es Freedom Homes Club por dentro y cuáles son los pasos para entrar, si te encaja.',
];

// Ecuación de valor: por qué merece la pena reservar
const VALUE = [
  { icon: Target, title: 'Una puerta real al mercado', text: 'Sales entendiendo una forma concreta de participar en el mercado inmobiliario que no pasa por hipotecarte con un piso entero.' },
  { icon: ShieldCheck, title: 'Con quien lo hace cada día', text: 'La da el equipo fundador del club, que trabaja operaciones reales y forma a los miembros. Nada de teoría de manual.' },
  { icon: Timer, title: 'En una sola tarde', text: 'Lo que llevaría meses de búsqueda por tu cuenta, explicado de principio a fin en una sesión en directo.' },
  { icon: Compass, title: 'Desde casa y desde cero', text: 'Te conectas por Zoom desde donde quieras. No necesitas experiencia ni preparar nada: solo tus preguntas.' },
];

const COMPARE = [
  ['Por dónde empiezas', 'Portales, foros y consejos sueltos', 'Formación desde cero con el equipo del club'],
  ['Qué necesitas', 'Comprar una vivienda entera, con todo lo que implica', 'Elegir en qué operación participas y cuánto aportas'],
  ['Qué oportunidades ves', 'Lo que ya ha llegado a un portal', 'Operaciones trabajadas fuera de los portales'],
  ['Quién analiza', 'Tú solo', 'Cada operación llega con su documentación y su estudio'],
  ['Quién decide', 'Tú, sin red', 'Tú, con información y una comunidad con criterio'],
];

const AGENDA = [
  ['El problema', 'Por qué comprar un piso entero se ha convertido en la única puerta que conoce la mayoría, y qué cuesta de verdad.'],
  ['La alternativa', 'Cómo funciona un club privado que trabaja operaciones inmobiliarias y forma a sus miembros para entenderlas.'],
  ['El criterio', 'Qué analizar en una operación antes de decidir: lo que separa una decisión informada de una apuesta.'],
  ['Tus preguntas', 'Turno abierto en directo con el equipo fundador. Pregunta lo que quieras.'],
];

const FOR_YES = [
  'Te interesa el mercado inmobiliario pero comprar un piso entero no es tu camino (o no lo es todavía).',
  'Quieres entender antes de decidir nada.',
  'Prefieres formarte y decidir tú, con información.',
  'Valoras aprender de un equipo que da la cara.',
];
const FOR_NO = [
  'Buscas dinero rápido o resultados garantizados.',
  'Quieres que otra persona decida por ti.',
  'No tienes una tarde para dedicarle.',
];

const TEAM = [
  { name: 'Antonio Gimeno', role: 'CCO y Director Comercial', img: '/team/antonio-gimeno.jpg' },
  { name: 'Daniel Serrano', role: 'COO', img: '/team/daniel-serrano.jpg' },
  { name: 'Luna Trejos', role: 'Directora de Formación y Comunidad', img: '/team/luna-trejos.jpg' },
];

const FAQ = [
  ['¿Tiene algún coste?', 'No. La masterclass es gratuita. Solo necesitas reservar tu plaza con tu nombre y tu email.'],
  ['¿Cómo me conecto?', 'Por Zoom. Te enviamos el enlace por email al reservar y te lo recordamos el día antes y una hora antes de empezar.'],
  ['¿Necesito experiencia previa?', 'No. Está pensada para empezar desde cero. Si ya sabes del sector, también te servirá para ver otra forma de hacerlo.'],
  ['¿Me vais a vender algo?', 'Al final te contamos cómo funciona el club por si quieres saber más. Sin presión: la decisión es siempre tuya.'],
  ['¿Qué hacéis con mis datos?', 'Solo los usamos para enviarte el acceso y los recordatorios de la masterclass. Puedes pedirnos que los borremos cuando quieras en info@freedomhomes.club.'],
  ['¿Puedo conectarme desde fuera de España?', 'Sí. Es online: puedes unirte desde cualquier país. La hora indicada es la de España.'],
];

function splitTitle(title) {
  const t = (title || DEFAULT_TITLE).trim();
  const i = t.toLowerCase().lastIndexOf(' sin ');
  return i > 0 ? [t.slice(0, i), t.slice(i + 1)] : [t, ''];
}

function useCountdown(iso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!iso) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [iso]);
  if (!iso) return null;
  const ms = new Date(iso).getTime() - now;
  if (ms <= 0) return { live: true };
  const s = Math.floor(ms / 1000);
  return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
}

function Countdown({ iso }) {
  const c = useCountdown(iso);
  if (!c) return null;
  if (c.live) return <p className="text-sm font-semibold text-[#e3c87a]" data-testid="mc-live"><Radio size={14} className="mr-1 inline" />¡Estamos en directo!</p>;
  const cells = [['días', c.d], ['horas', c.h], ['min', c.m], ['seg', c.s]];
  return (
    <div className="flex justify-center gap-2" aria-label="Tiempo hasta la masterclass" data-testid="mc-countdown">
      {cells.map(([l, v]) => (
        <div key={l} className="w-16 rounded-xl border border-white/10 bg-white/[0.03] py-2 text-center">
          <p className="text-xl font-semibold tabular-nums text-white">{String(v).padStart(2, '0')}</p>
          <p className="text-[10px] uppercase tracking-wider text-gray-400">{l}</p>
        </div>
      ))}
    </div>
  );
}

function scrollToForm() {
  const el = document.getElementById('reserva');
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  setTimeout(() => document.getElementById('mc-name')?.focus({ preventScroll: true }), 600);
}

function CtaButton({ children = 'Reservar mi plaza gratis', size = 'lg', testid = 'mc-cta' }) {
  const pad = size === 'lg' ? 'px-7 py-4 text-base' : 'px-5 py-2.5 text-sm';
  return (
    <button type="button" onClick={scrollToForm} data-testid={testid}
      className={`group inline-flex max-w-full items-center justify-center gap-2.5 rounded-full text-center bg-gradient-to-r from-[#c9a84c] to-[#e3c87a] ${pad} font-semibold text-[#0a1225] shadow-[0_10px_40px_-10px_rgba(201,168,76,0.6)] transition-transform hover:-translate-y-0.5`}>
      {children}
      <ArrowRight size={size === 'lg' ? 18 : 15} className="transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

function SectionTitle({ eyebrow, title, sub }) {
  return (
    <motion.div {...reveal} className="mx-auto mb-10 max-w-3xl text-center md:mb-14">
      {eyebrow && <p className={`mb-3 text-[11px] font-semibold uppercase tracking-[0.3em] ${GOLD}`}>{eyebrow}</p>}
      <h2 style={serif} className="text-3xl leading-tight text-white md:text-[2.6rem]">{title}</h2>
      {sub && <p className="mt-4 text-base leading-relaxed text-gray-400 md:text-lg">{sub}</p>}
    </motion.div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.07]">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="flex min-h-[44px] w-full items-center justify-between gap-4 py-5 text-left text-white hover:text-[#e3c87a]">
        <span className="font-medium">{q}</span>
        <ChevronDown size={18} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${GOLD}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden pb-5 text-sm leading-relaxed text-gray-400">{a}</motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

const inputCls = 'w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-[15px] text-white placeholder:text-gray-500 focus:border-[#c9a84c]/50 focus:outline-none';

// Comunidad de WhatsApp (02/11/2026). La API la manda en event.community_url; esta es la de respaldo.
const COMMUNITY_URL = 'https://chat.whatsapp.com/GuEZBGyo50k25d4GvQEtqR';

function RegisterCard({ event, referralCode, affiliateName }) {
  const formToken = useFormToken();
  const [form, setForm] = useState({ name: '', email: '', website: '' });
  const [state, setState] = useState({ sending: false, error: '', done: null });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!event) return;
    if (form.name.trim().length < 2) { setState((s) => ({ ...s, error: 'Escribe tu nombre' })); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) { setState((s) => ({ ...s, error: 'Revisa tu email' })); return; }
    setState({ sending: true, error: '', done: null });
    try {
      const token = await formToken();
      const res = await fetch(`${API}/api/events/${encodeURIComponent(event.slug)}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(), email: form.email.trim(), ticket_type: 'libre',
          referral_code: referralCode || undefined, form_token: token || undefined, website: form.website || undefined,
        }),
      });
      if (res.ok || res.status === 409) {
        setState({ sending: false, error: '', done: res.status === 409 ? 'again' : 'new' });
        return;
      }
      const err = await res.json().catch(() => ({}));
      setState({ sending: false, error: typeof err.detail === 'string' ? err.detail : 'No hemos podido reservar tu plaza. Inténtalo de nuevo.', done: null });
    } catch {
      setState({ sending: false, error: 'No hemos podido conectar. Revisa tu conexión e inténtalo de nuevo.', done: null });
    }
  };

  if (state.done) {
    return (
      <div className="text-center" data-testid="mc-done">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#c9a84c]/15"><MailCheck size={26} className={GOLD} /></div>
        <p style={serif} className="mt-4 text-2xl text-white">{state.done === 'again' ? 'Ya tenías tu plaza' : '¡Plaza reservada!'}</p>
        <p className="mt-2 text-sm leading-relaxed text-gray-300">
          Te hemos enviado un email a <span className="text-white">{form.email.trim()}</span> con todos los detalles.
          Si no lo ves en unos minutos, mira en promociones o spam.
        </p>
        {event?.when && <p className="mt-3 text-sm text-gray-400">{event.when}</p>}
        <div className="mt-6 rounded-2xl border border-[#25D366]/30 bg-[#25D366]/[0.08] p-4 text-left" data-testid="mc-community">
          <p className="text-sm font-semibold text-white">Último paso: únete a la comunidad</p>
          <p className="mt-1 text-sm leading-relaxed text-gray-300">
            Entra en nuestra comunidad de WhatsApp y no te pierdas nada: avisos de la masterclass, novedades del club y las próximas sesiones.
          </p>
          <a href={event?.community_url || COMMUNITY_URL} target="_blank" rel="noopener noreferrer" data-testid="mc-community-link"
            className="mt-3 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-[#25D366] px-3 py-3 text-sm font-semibold text-[#0a1628] hover:bg-[#3ee07a]">
            <MessageCircle size={17} className="shrink-0" />
            <span className="sm:hidden">Entrar en la comunidad</span>
            <span className="hidden sm:inline">Entrar en la comunidad de WhatsApp</span>
          </a>
        </div>
        {event?.calendar_url && (
          <a href={event.calendar_url} target="_blank" rel="noopener noreferrer" data-testid="mc-calendar"
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#c9a84c]/40 px-5 py-2.5 text-sm font-semibold text-[#e3c87a] hover:bg-[#c9a84c]/10">
            <CalendarPlus size={16} /> Añadir a mi calendario
          </a>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="relative space-y-3" noValidate data-testid="mc-form">
      {affiliateName && (
        <p className="flex items-center gap-2 rounded-xl border border-[#c9a84c]/25 bg-[#c9a84c]/[0.08] px-3 py-2 text-xs text-gray-300">
          <UserCheck size={15} className={GOLD} /> Te invita <strong className="text-[#e3c87a]">{affiliateName}</strong>
        </p>
      )}
      <div aria-hidden="true" style={honeypotStyle}>
        <label>Web <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} /></label>
      </div>
      <label className="block">
        <span className="sr-only">Tu nombre</span>
        <input id="mc-name" value={form.name} onChange={set('name')} autoComplete="given-name" placeholder="Tu nombre"
          className={inputCls} maxLength={120} disabled={!event || state.sending} data-testid="mc-name" />
      </label>
      <label className="block">
        <span className="sr-only">Tu email</span>
        <input type="email" value={form.email} onChange={set('email')} autoComplete="email" inputMode="email" placeholder="Tu email"
          className={inputCls} maxLength={200} disabled={!event || state.sending} data-testid="mc-email" />
      </label>
      {state.error && <p className="text-sm text-red-300" role="alert" data-testid="mc-error">{state.error}</p>}
      <button type="submit" disabled={!event || state.sending} data-testid="mc-submit"
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#c9a84c] to-[#e3c87a] py-4 text-base font-semibold text-[#0a1225] shadow-[0_10px_40px_-10px_rgba(201,168,76,0.6)] disabled:opacity-60">
        {state.sending ? <Loader2 size={18} className="animate-spin" /> : null}
        {state.sending ? 'Reservando…' : 'Quiero mi plaza gratis'}
        {!state.sending && <ArrowRight size={18} />}
      </button>
      <p className="text-center text-[11px] leading-relaxed text-gray-500">
        Te enviamos el enlace de Zoom por email. Al reservar aceptas la <a href="/privacy-policy-fhc" className="underline hover:text-[#c9a84c]">política de privacidad</a>.
      </p>
    </form>
  );
}

export default function MasterclassEnDirecto() {
  const { referralCode, affiliateName } = useReferral();
  const [event, setEvent] = useState(undefined); // undefined = cargando · null = no hay ninguna anunciada
  const [showBar, setShowBar] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/events/masterclass/next`)
      .then((r) => (r.ok ? r.json() : { event: null }))
      .then((d) => { if (alive) setEvent(d.event || null); })
      .catch(() => { if (alive) setEvent(null); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Masterclass en directo · Freedom Homes Club';
    return () => { document.title = prev; };
  }, []);

  // Barra fija en el móvil cuando el formulario ya no se ve
  useEffect(() => {
    const el = formRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => setShowBar(!e.isIntersecting && window.scrollY > 300));
    io.observe(el);
    return () => io.disconnect();
  }, [event]);

  const [t1, t2] = splitTitle(event?.title);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#060d1b] text-white" data-testid="masterclass-en-directo">
      <header className="sticky top-0 z-40 border-b border-white/[0.05] bg-[#060d1b]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <a href="/" aria-label="Freedom Homes Club"><img src="/logo-fhc-sm.png" alt="Freedom Homes Club" className="h-11 w-auto" /></a>
          {event && <CtaButton size="sm" testid="mc-cta-header">Reservar plaza</CtaButton>}
        </div>
      </header>

      {/* Promesa + registro */}
      <section className="relative">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[620px] bg-[radial-gradient(ellipse_at_top,rgba(201,168,76,0.16),transparent_60%)]" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-7 px-5 pb-16 pt-7 sm:gap-10 md:pt-16 lg:grid-cols-[1.15fr_1fr] lg:gap-14">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center lg:text-left">
            <p className="inline-flex items-center gap-2 rounded-full border border-[#c9a84c]/30 bg-[#c9a84c]/[0.08] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#e3c87a]">
              <Radio size={12} /> Masterclass gratuita en directo
            </p>
            <h1 style={serif} className="mt-5 text-[1.85rem] leading-[1.12] sm:text-[2.4rem] md:text-[3.4rem]" data-testid="mc-title">
              {t1}
              {t2 && <span className="mt-2 block text-transparent bg-clip-text bg-gradient-to-r from-[#c9a84c] via-[#e3c87a] to-[#c9a84c]">{t2}.</span>}
            </h1>
            {/* En el móvil el formulario va antes: la promesa ampliada se queda para pantallas grandes */}
            <p className="mx-auto mt-6 hidden max-w-xl text-base leading-relaxed text-gray-300 sm:block md:text-lg lg:mx-0">
              {event?.subtitle || 'El equipo fundador de Freedom Homes Club te enseña, en directo y desde cero, cómo un club privado abre la puerta a operaciones inmobiliarias reales sin tener que comprar una vivienda entera. Y tú decides en cada paso.'}
            </p>
            {event && (
              <div className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-gray-300 sm:mt-6 lg:justify-start" data-testid="mc-when">
                <span className="inline-flex items-center gap-2"><CalendarDays size={16} className={GOLD} /> {event.when.split(' a las ')[0]}</span>
                <span className="inline-flex items-center gap-2"><Clock size={16} className={GOLD} /> {event.when.split(' a las ')[1]}</span>
                <span className="inline-flex items-center gap-2"><Video size={16} className={GOLD} /> Online por Zoom</span>
              </div>
            )}
          </motion.div>

          <motion.div id="reserva" ref={formRef} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="mx-auto w-full max-w-md rounded-3xl border border-[#c9a84c]/25 bg-[#0b1830]/90 p-6 shadow-[0_30px_120px_-30px_rgba(201,168,76,0.45)] md:p-8">
            {event === undefined ? (
              <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#c9a84c]" size={26} /></div>
            ) : event === null ? (
              <div className="py-6 text-center" data-testid="mc-none">
                <p style={serif} className="text-2xl text-white">Muy pronto, la próxima</p>
                <p className="mt-3 text-sm leading-relaxed text-gray-300">Estamos preparando la próxima masterclass en directo. Mientras tanto, puedes ver la última.</p>
                <a href="/masterclass" className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#c9a84c]/40 px-5 py-2.5 text-sm font-semibold text-[#e3c87a] hover:bg-[#c9a84c]/10">
                  Ver la última masterclass <ArrowRight size={15} />
                </a>
              </div>
            ) : (
              <>
                <p style={serif} className="text-center text-2xl text-white">Reserva tu plaza gratis</p>
                <p className="mb-5 mt-1 text-center text-sm text-gray-400">Solo tu nombre y tu email.</p>
                <div className="mb-5"><Countdown iso={event.starts_at} /></div>
                <RegisterCard event={event} referralCode={referralCode} affiliateName={affiliateName} />
              </>
            )}
          </motion.div>
        </div>
      </section>

      {/* Qué vas a descubrir */}
      <section className="border-y border-white/[0.05] bg-[#081325] py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-5">
          <SectionTitle eyebrow="En la masterclass" title="Lo que vas a descubrir en directo." />
          <motion.ul {...reveal} className="space-y-4">
            {LEARN.map((d) => (
              <li key={d} className="flex items-start gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 text-gray-200">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#c9a84c]/15"><Check size={14} className={GOLD} strokeWidth={3} /></span>
                <span className="leading-relaxed">{d}</span>
              </li>
            ))}
          </motion.ul>
          {event && <div className="mt-10 text-center"><CtaButton testid="mc-cta-learn" /></div>}
        </div>
      </section>

      {/* Ecuación de valor */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <SectionTitle eyebrow="Por qué reservar" title={<>Una tarde. <span className={GOLD}>Una forma nueva de ver el mercado.</span></>}
            sub="Una sola sesión para entender algo que la mayoría tarda años en descubrir, si lo descubre." />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUE.map((v, i) => (
              <motion.div key={v.title} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }}
                className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-7">
                <v.icon size={26} className={GOLD} />
                <h3 className="mt-5 text-lg font-semibold text-white">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-400">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* El viejo camino frente al nuevo */}
      <section className="border-y border-white/[0.05] bg-[#081325] py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5">
          <SectionTitle eyebrow="La diferencia" title="Comprar un piso entero no es la única puerta." />
          <motion.div {...reveal} className="overflow-hidden rounded-3xl border border-white/[0.08]">
            <div className="grid grid-cols-[1fr_1fr] border-b border-white/[0.08] bg-white/[0.03] text-sm font-semibold md:grid-cols-[0.8fr_1fr_1fr]">
              <span className="hidden px-5 py-4 text-gray-500 md:block" />
              <span className="px-5 py-4 text-gray-300">Por tu cuenta</span>
              <span className={`px-5 py-4 ${GOLD}`}>Con un club privado</span>
            </div>
            {COMPARE.map(([k, a, b]) => (
              <div key={k} className="grid grid-cols-[1fr_1fr] border-b border-white/[0.05] text-sm last:border-b-0 md:grid-cols-[0.8fr_1fr_1fr]">
                <span className="col-span-2 px-5 pt-4 text-[11px] font-semibold uppercase tracking-wider text-gray-500 md:col-span-1 md:py-4 md:text-xs md:normal-case md:tracking-normal md:text-gray-400">{k}</span>
                <span className="flex gap-2 px-5 py-4 text-gray-400"><X size={16} className="mt-0.5 flex-shrink-0 text-gray-600" />{a}</span>
                <span className="flex gap-2 px-5 py-4 text-gray-200"><Check size={16} className={`mt-0.5 flex-shrink-0 ${GOLD}`} strokeWidth={3} />{b}</span>
              </div>
            ))}
          </motion.div>
          {event && <div className="mt-10 text-center"><CtaButton testid="mc-cta-compare">Quiero verlo en directo</CtaButton></div>}
        </div>
      </section>

      {/* Cómo va la sesión */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5">
          <SectionTitle eyebrow="Cómo va la sesión" title="Cuatro partes. Y tus preguntas en directo." />
          <div className="grid gap-5 md:grid-cols-2">
            {AGENDA.map(([t, d], i) => (
              <motion.div key={t} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }}
                className="flex gap-5 rounded-3xl border border-white/[0.06] bg-white/[0.02] p-7">
                <span style={serif} className={`text-4xl leading-none ${GOLD}`}>{i + 1}</span>
                <div>
                  <h3 className="text-lg font-semibold text-white">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-400">{d}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div {...reveal} className="mx-auto mt-10 max-w-2xl rounded-3xl border border-[#c9a84c]/25 bg-[#c9a84c]/[0.05] p-7 text-center">
            <MessageCircle size={24} className={`mx-auto ${GOLD}`} />
            <p style={serif} className="mt-3 text-2xl text-white">En directo y con tus preguntas</p>
            <p className="mt-3 leading-relaxed text-gray-300">
              Es una sesión en vivo con el equipo fundador. Lo mejor pasa en el turno de preguntas: conéctate a la hora y pregunta lo que quieras.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Para quién es */}
      <section className="border-y border-white/[0.05] bg-[#081325] py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5">
          <SectionTitle eyebrow="Para quién es" title="No es para todo el mundo. Y está bien que así sea." />
          <div className="grid gap-5 md:grid-cols-[1.4fr_1fr]">
            <motion.div {...reveal} className="rounded-3xl border border-[#c9a84c]/25 bg-[#c9a84c]/[0.05] p-7">
              <p className="text-lg font-semibold text-white">Reserva tu plaza si…</p>
              <ul className="mt-4 space-y-3">
                {FOR_YES.map((t) => <li key={t} className="flex gap-3 text-gray-300"><Check size={18} className={`mt-0.5 flex-shrink-0 ${GOLD}`} strokeWidth={3} />{t}</li>)}
              </ul>
            </motion.div>
            <motion.div {...reveal} className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-7">
              <p className="text-lg font-semibold text-white">Mejor no vengas si…</p>
              <ul className="mt-4 space-y-3">
                {FOR_NO.map((t) => <li key={t} className="flex gap-3 text-gray-400"><X size={18} className="mt-0.5 flex-shrink-0 text-gray-500" />{t}</li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Equipo */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5">
          <SectionTitle eyebrow="Quién la imparte" title="El equipo fundador, en directo." />
          <div className="mx-auto flex max-w-3xl flex-wrap justify-center gap-5">
            {TEAM.map((p, i) => (
              <motion.div key={p.name} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }} className="w-[calc(50%-10px)] text-center md:w-[calc(33.333%-13.34px)]">
                <img src={p.img} alt={p.name} loading="lazy" className="mx-auto aspect-square w-full max-w-[180px] rounded-3xl border border-white/[0.08] object-cover grayscale-[20%]" />
                <p className="mt-4 font-semibold text-white">{p.name}</p>
                <p className="mt-0.5 text-xs text-gray-400">{p.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Preguntas */}
      <section className="border-t border-white/[0.05] bg-[#081325] py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-5">
          <SectionTitle eyebrow="Preguntas frecuentes" title="Antes de reservar." />
          <motion.div {...reveal} className="border-t border-white/[0.07]">
            {FAQ.map(([q, a]) => <FaqItem key={q} q={q} a={a} />)}
          </motion.div>
        </div>
      </section>

      {/* CTA final */}
      {event && (
        <section className="relative overflow-hidden pb-24 pt-16">
          <div className="mx-auto max-w-4xl px-5">
            <motion.div {...reveal} className="relative overflow-hidden rounded-[2rem] border border-[#c9a84c]/30 bg-gradient-to-br from-[#13284a] via-[#0a1628] to-[#060d1b] px-6 py-14 text-center md:px-14">
              <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-[36rem] -translate-x-1/2 rounded-full bg-[#c9a84c]/20 blur-3xl" />
              <p className={`relative text-[11px] font-semibold uppercase tracking-[0.3em] ${GOLD}`}>{event.when}</p>
              <h2 style={serif} className="relative mt-4 text-3xl leading-tight md:text-5xl">El mercado inmobiliario<br />no tiene por qué empezar por un piso entero.</h2>
              <p className="relative mx-auto mt-5 max-w-xl text-gray-300">Reserva tu plaza gratis y conéctate en directo con el equipo de Freedom Homes Club.</p>
              <div className="relative mt-8"><CtaButton testid="mc-cta-final" /></div>
              <div className="relative mt-6"><Countdown iso={event.starts_at} /></div>
            </motion.div>
          </div>
        </section>
      )}

      <footer className="border-t border-white/[0.05] py-10 pb-24 md:pb-10">
        <div className="mx-auto max-w-4xl space-y-3 px-5 text-center text-[11px] leading-relaxed text-gray-500">
          <p>
            Freedom Homes Club es un club privado de formación y comunidad. La masterclass es divulgativa: no constituye una oferta
            pública ni una recomendación, y no garantiza resultados. Cada miembro decide por sí mismo, después de formarse.
          </p>
          <p>© {new Date().getFullYear()} Freedom Life Club LLC · info@freedomhomes.club · <a href="/terms-conditions" className="hover:text-[#c9a84c]">Términos y condiciones</a> · <a href="/privacy-policy-fhc" className="hover:text-[#c9a84c]">Privacidad</a></p>
        </div>
      </footer>

      <AnimatePresence>
        {event && showBar && (
          <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }} transition={{ duration: 0.25 }}
            className="fixed inset-x-0 bottom-0 z-50 border-t border-[#c9a84c]/20 bg-[#060d1b]/95 px-4 py-3 backdrop-blur md:hidden">
            <button type="button" onClick={scrollToForm} data-testid="mc-cta-sticky"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#c9a84c] to-[#e3c87a] py-3.5 font-semibold text-[#0a1225]">
              Reservar mi plaza gratis <ArrowRight size={17} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
